// @flow
import React, { Fragment } from 'react';

import Link from 'app/common/components/Link';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import radio from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Radio';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import {
  BASE_DOC_URL_VB,
  PARTNER_SOLUTIONS_BASE_DOC_URL,
} from 'app/developer-v3/constants';
import {
  getUserFields,
  messageField,
} from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

import { commonStyles } from '../commonStyles';

export const createEmbeddingForPartners = (isLoggedIn: boolean) => ({
  value: 'zap-templates-and-embedding',
  label: 'Embedding for Partners',
  zendeskCustomFieldValue: 'pcf-existing-embedding',
  child: radio({
    id: 'zaptemplates',
    label: "Let's get you some help",
    zendeskCustomFieldId: 360044582732,
    items: [
      {
        value: 'promoting-zap-templates',
        label: 'Promoting  and embedding Zap templates',
        zendeskCustomFieldValue: 'pcf-existing-embedding-promoting',
        child: help({
          docs: {
            title: 'Promoting Zap templates',
            body: () => (
              <Fragment>
                <p>
                  The fastest way to grow on Zapier is to surface the best
                  use-cases when and where users need them via embedded Zap
                  Templates in your app and on your marketing pages via Zapier
                  Elements.
                </p>
                <ul style={commonStyles.ul}>
                  <li>
                    Users can set up their Zaps from a pop up window, keeping
                    them in your product experience.
                  </li>
                  <li>
                    Your Element will automatically update to show the most
                    popular Zap templates for your product.
                  </li>
                  <li>
                    Present users with the most popular Zap templates in the
                    most contextual places within your product.
                  </li>
                  <li>
                    One small snippet of Javascript and you can display your Zap
                    Templates anywhere. A powerful Partner API is available for
                    even more customizability to suit any interface.
                  </li>
                </ul>
                <iframe
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen={true}
                  frameborder="0"
                  height="315"
                  src="https://www.youtube.com/embed/xWwECaAfdPo"
                  title=" Zap Templates Element YouTube video player"
                  width="560"
                ></iframe>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/zap-templates`,
                title: 'Zap templates',
              },
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-api/list-zap-templates`,
                title: 'Embed Zap templates',
              },
              {
                href: `${ZAPIER_APP_BASE_URL}/developer-platform/embed-tools`,
                title: 'Discover the right solutions for your product',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about promoting your Zap templates.`,
            },
          ],
        }),
      },
      {
        value: 'embedding-elements',
        label: 'Embedding with Zapier Elements',
        zendeskCustomFieldValue: 'pcf-existing-embedding-elements',
        child: help({
          docs: {
            title: 'Embedding with the Zapier Elements',
            body: () => (
              <Fragment>
                <p>
                  Zapier <span style={commonStyles.emphasis}>Elements</span>{' '}
                  allows you to embed specific pieces of Zapier within your
                  product in just minutes by copy pasting some code.
                </p>
                <p>
                  Elements are prebuilt UI components that offer the
                  quickest—and easiest—way to surface your Zapier integration,
                  available app connections, popular Zap templates, and Zaps
                  your users set up inside your product.
                </p>
                <iframe
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen={true}
                  frameborder="0"
                  height="315"
                  src="https://www.youtube.com/embed/videoseries?list=PLcFpLbKfavFy0qMdvkfwB16lHcI668uJ3"
                  title="Zapier Partner Embedded YouTube video player"
                  width="560"
                ></iframe>
              </Fragment>
            ),
            links: [
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-element/intro`,
                title: 'Embed the Workflow Element',
              },
              {
                href: `${ZAPIER_APP_BASE_URL}/developer-platform/embed-tools`,
                title: 'Discover the right solutions for your product',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about embedding with the Javascript widget.`,
            },
          ],
        }),
      },
      {
        value: 'embedding-api',
        label: 'Embedding with the Partner API',
        zendeskCustomFieldValue: 'pcf-existing-embedding-partner-api',
        child: help({
          docs: {
            title: 'Embedding with the Workflow API',
            body: () => (
              <Fragment>
                <p>
                  The <span style={commonStyles.emphasis}>Partner API</span> is
                  for partners that wish to have more flexibility and control
                  over a user’s experience with Zapier within their product.
                  With the Partner API, you can:
                </p>
                <ul style={commonStyles.ul}>
                  <li>
                    <span style={commonStyles.emphasis}>
                      Have complete style control
                    </span>{' '}
                    over how you present Zap templates in your product. The
                    Partner API gives you access to the raw Zap template data so
                    you can give your users access to your Zap template with
                    your product’s style, look and feel.
                  </li>
                  <li>
                    <span style={commonStyles.emphasis}>
                      Get access to all your Zap templates
                    </span>{' '}
                    and give your users the ability to search to quickly find
                    the one they need.
                  </li>
                  <li>
                    <span style={commonStyles.emphasis}>
                      Streamline Zap setup
                    </span>{' '}
                    by pre-filling fields on behalf of your users.
                  </li>
                  <li>
                    <span style={commonStyles.emphasis}>
                      Show users the Zaps they have
                    </span>{' '}
                    set up from right within your product keeping them on your
                    site longer and giving them complete confidence in their
                    Zapier integration.
                  </li>
                  <li>
                    <span style={commonStyles.emphasis}>
                      Get a list of all the apps available in Zapier’s app
                      directory
                    </span>{' '}
                    so you can power your app directory and show your users all
                    the integration possibilities with your Zapier integration.
                  </li>
                </ul>
                <p>
                  It is intended for use only for public apps on our platform
                  that have been tested by our team. Details on how to publicly
                  launch your app{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements`}
                    target="_blank"
                  >
                    here
                  </Link>
                  .
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-api/intro`,
                title: 'Workflow API Overview',
              },
              {
                href: `${ZAPIER_APP_BASE_URL}/developer-platform/embed-tools`,
                title: 'Discover the right solutions for your product',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about embedding with the Partner API.`,
            },
          ],
        }),
      },
    ],
  }),
});
